import { useState } from 'react';
import { SelectorResponse } from '@shared/interfaces/api/trackerRequest';
import { deleteSelector, updateSelector } from '@services/tracker/tracker.service';
import './SelectorList.sass';

interface SelectorListProps {
  selectors: SelectorResponse[];
  onAddSelector: () => void;
  onEditSelector: (selector: SelectorResponse) => void;
  onSelectorDeleted: () => void;
  projectId: string;
}

function SelectorList({
  selectors,
  onAddSelector,
  onEditSelector,
  onSelectorDeleted,
  projectId,
}: SelectorListProps) {
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const handleDelete = async (selectorId: number) => {
    if (!window.confirm('Are you sure you want to delete this selector?')) {
      return;
    }

    setDeletingId(selectorId);

    const response = await deleteSelector(selectorId);

    if (response.error) {
      alert(response.error);
      setDeletingId(null);
      return;
    }

    setDeletingId(null);
    onSelectorDeleted();
  };

  const handleToggleActive = async (selector: SelectorResponse) => {
    setTogglingId(selector.id);

    const response = await updateSelector(selector.id, {
      is_active: !selector.is_active,
    });

    if (response.error) {
      alert(response.error);
      setTogglingId(null);
      return;
    }

    setTogglingId(null);
    onSelectorDeleted(); // Refresh the list
  };

  const getTrackingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      click: 'Click',
      visibility: 'Visibility',
      hover: 'Hover',
      form_submit: 'Form Submit',
      value_change: 'Value Change',
      focus: 'Focus',
      blur: 'Blur',
      scroll_into_view: 'Scroll Into View',
    };
    return labels[type] || type;
  };

  return (
    <div className="selector-list">
      <div className="list-header">
        <h2>Selectors ({selectors.length})</h2>
        <button className="btn-add" onClick={onAddSelector} type="button">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Add Selector
        </button>
      </div>

      {selectors.length === 0 ? (
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <rect x="8" y="16" width="48" height="32" rx="4" stroke="#d1d5db" strokeWidth="2" fill="none"/>
            <line x1="8" y1="24" x2="56" y2="24" stroke="#d1d5db" strokeWidth="2"/>
            <circle cx="16" cy="20" r="2" fill="#d1d5db"/>
            <circle cx="24" cy="20" r="2" fill="#d1d5db"/>
            <circle cx="32" cy="20" r="2" fill="#d1d5db"/>
          </svg>
          <h3>No selectors yet</h3>
          <p>Add your first selector to start tracking user interactions</p>
          <button className="btn-primary" onClick={onAddSelector} type="button">
            Add Selector
          </button>
        </div>
      ) : (
        <div className="selectors-grid">
          {selectors.map((selector) => (
            <div
              key={selector.id}
              className={`selector-card ${!selector.is_active ? 'inactive' : ''}`}
            >
              <div className="card-header">
                <div className="selector-name">
                  <h3>{selector.event_name}</h3>
                  <span className="selector-type">{selector.selector_type.toUpperCase()}</span>
                </div>
                <div className="card-actions">
                  <button
                    className={`toggle-btn ${selector.is_active ? 'active' : 'inactive'}`}
                    onClick={() => handleToggleActive(selector)}
                    disabled={togglingId === selector.id}
                    type="button"
                    title={selector.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {togglingId === selector.id ? (
                      <span className="spinner"></span>
                    ) : (
                      <div className={`toggle-switch ${selector.is_active ? 'active' : ''}`}>
                        <div className="toggle-knob"></div>
                      </div>
                    )}
                  </button>
                  <button
                    className="icon-btn"
                    onClick={() => onEditSelector(selector)}
                    type="button"
                    title="Edit"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M11.3333 2L14 4.66667L5.33333 13.3333H2.66667V10.6667L11.3333 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    className="icon-btn delete"
                    onClick={() => handleDelete(selector.id)}
                    disabled={deletingId === selector.id}
                    type="button"
                    title="Delete"
                  >
                    {deletingId === selector.id ? (
                      <span className="spinner"></span>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 4H13M5 4V3C5 2.44772 5.44772 2 6 2H10C10.5523 2 11 2.44772 11 3V4M6.5 7V11M9.5 7V11M4 4H12V13C12 13.5523 11.5523 14 11 14H5C4.44772 14 4 13.5523 4 13V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="card-body">
                <div className="selector-value">
                  <code>{selector.selector_value}</code>
                </div>
                
                <div className="selector-meta">
                  <span className="tracking-type">
                    {getTrackingTypeLabel(selector.tracking_type)}
                  </span>
                  {selector.description && (
                    <span className="description">{selector.description}</span>
                  )}
                </div>

                <div className="selector-dates">
                  <span>Created: {new Date(selector.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SelectorList;
